import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useStaff } from '../lib/auth';
import { duration, time } from '../lib/format';
import { SHIFT_ROLES, useClock, useMyPresence } from '../lib/presence';
import { Button, cn, Modal, Notice, useToast } from './ui';

/**
 * Clock in / Clock out (staff presence). Signing in does not make someone "present": they start their shift with
 * Clock in, and the super admin (every shop) and their shop admin (own shop) see who is working now.
 *
 * - ShiftButton sits in the top bar for every login that works shifts (gate, reception, venue staff, shop admins
 *   and counters): "Clock in", or "On duty · 2h 05m" which opens the Clock out confirmation.
 * - ClockInPrompt is the "Clock in now?" offer shown above the first page after signing in, until the person clocks
 *   in, taps Not now or moves on to another page (remembered for the business day in this browser tab). It never
 *   follows them onto task screens such as Collect payment, where it would push the pay button off a phone screen;
 *   the top-bar Clock in stays there.
 */

function useShiftOutlet() {
  const { me, outletId } = useStaff();
  return outletId && me.outlets.some((o) => o.id === outletId) ? outletId : null;
}

export function ShiftButton() {
  const { me } = useStaff();
  const shiftRole = SHIFT_ROLES.includes(me.user.role);
  const q = useMyPresence(shiftRole);
  const clock = useClock();
  const toast = useToast();
  const outletId = useShiftOutlet();
  const [confirm, setConfirm] = useState(false);
  if (!shiftRole || !q.data) return null;
  const shift = q.data.shift;

  const clockIn = () =>
    clock.mutate(
      { action: 'clock-in', outletId },
      { onSuccess: () => toast.success('Clocked in. Have a good shift.'), onError: (e) => toast.error(e) },
    );
  const clockOut = () =>
    clock.mutate(
      { action: 'clock-out' },
      {
        onSuccess: (d) => {
          setConfirm(false);
          toast.success(`Clocked out · ${duration(d.todayMinutes)} today`);
        },
        onError: (e) => toast.error(e),
      },
    );

  return (
    <>
      <button
        type="button"
        onClick={shift ? () => setConfirm(true) : clockIn}
        disabled={clock.isPending}
        data-testid="shift-button"
        className={cn(
          'inline-flex h-11 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium ring-1 sm:h-9',
          shift ? 'bg-emerald-50 text-emerald-800 ring-emerald-200 hover:bg-emerald-100' : 'bg-white text-slate-700 ring-slate-300 hover:bg-slate-50',
        )}
        title={shift ? `Clocked in at ${time(shift.clockInAt)}` : 'Start your shift'}
      >
        <span aria-hidden className={cn('h-2 w-2 rounded-full', shift ? 'bg-emerald-500' : 'bg-slate-300')} />
        {shift ? (
          <span>
            <span className="hidden sm:inline">On duty · </span>
            {duration(q.data.todayMinutes)}
          </span>
        ) : (
          'Clock in'
        )}
      </button>
      <Modal
        open={confirm && !!shift}
        onClose={() => setConfirm(false)}
        title="Clock out?"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirm(false)}>
              Stay clocked in
            </Button>
            <Button onClick={clockOut} loading={clock.isPending}>
              Clock out
            </Button>
          </div>
        }
      >
        {shift && (
          <p className="text-sm text-slate-700">
            You clocked in at <strong>{time(shift.clockInAt)}</strong>
            {shift.outletName ? ` at ${shift.outletName}` : ''}. Worked today: <strong>{duration(q.data.todayMinutes)}</strong>.
          </p>
        )}
      </Modal>
    </>
  );
}

const promptKey = (userId: string, day: string) => `clock-in-prompt:${userId}:${day}`;

function dismissed(key: string) {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/** The page the prompt was first shown on (this business day, this tab). */
function shownOn(key: string) {
  try {
    return sessionStorage.getItem(`${key}:on`);
  } catch {
    return null;
  }
}

export function ClockInPrompt() {
  const { me } = useStaff();
  const shiftRole = SHIFT_ROLES.includes(me.user.role);
  const q = useMyPresence(shiftRole);
  const clock = useClock();
  const toast = useToast();
  const outletId = useShiftOutlet();
  const [hidden, setHidden] = useState(false);
  const { pathname } = useLocation();
  const key = q.data ? promptKey(me.user.id, q.data.businessDate) : null;
  const seenOn = key ? shownOn(key) : null;
  const show = shiftRole && !!q.data && !q.data.shift && !hidden && !!key && !dismissed(key) && (!seenOn || seenOn === pathname);
  useEffect(() => {
    if (!show || !key || seenOn) return;
    try {
      sessionStorage.setItem(`${key}:on`, pathname);
    } catch {
      /* private window: shown on every page */
    }
  }, [show, key, seenOn, pathname]);
  if (!show || !key) return null;
  const notNow = () => {
    try {
      sessionStorage.setItem(key, '1');
    } catch {
      /* private window: hide for this page view only */
    }
    setHidden(true);
  };

  return (
    <div className="mb-4" data-testid="clock-in-prompt">
      <Notice tone="green">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <p className="min-w-0 flex-1">
            <strong className="font-semibold">Clock in now?</strong> Start your shift so your {me.user.role === 'shop_staff' ? 'shop admin' : 'admin'} sees you are working.
            {q.data.lastAutoClosedAt && (
              <span className="block text-xs">Your last shift was closed by the system at {time(q.data.lastAutoClosedAt)} because you did not clock out.</span>
            )}
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={notNow}>
              Not now
            </Button>
            <Button
              size="sm"
              loading={clock.isPending}
              onClick={() => clock.mutate({ action: 'clock-in', outletId }, { onSuccess: () => toast.success('Clocked in. Have a good shift.'), onError: (e) => toast.error(e) })}
            >
              Clock in
            </Button>
          </div>
        </div>
      </Notice>
    </div>
  );
}
